import { useContext, useEffect, useState, useCallback } from "react";
import Repo from "../../Utility/Repo";
import { TokenContext } from "../../Components/TokenContext";
import Loader from "../../Components/Loader";

const LIMIT_OPTIONS = [
  { label: "Ultimi 50", value: 50 },
  { label: "Ultimi 100", value: 100 },
  { label: "Ultimi 150", value: 150 },
  { label: "Tutti", value: 0 },
];

const TIPO_LABEL = { 0: "ANPR", 1: "INPS" };

function AggiornamentoMassivo(props) {
  const apiToken = useContext(TokenContext);

  const [loading, setLoading] = useState(false);
  const [statoCoda, setStatoCoda] = useState(null);
  const [utentiAnagrafica, setUtentiAnagrafica] = useState([]);
  const [utentiIsee, setUtentiIsee] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [changelogFilter, setChangelogFilter] = useState("");
  const [soloErrori, setSoloErrori] = useState(false);
  const [limitAnpr, setLimitAnpr] = useState(50);
  const [limitInps, setLimitInps] = useState(50);
  const [alertMsg, setAlertMsg] = useState(null);
  const [alertType, setAlertType] = useState("success");

  const showAlert = (msg, type = "success") => {
    setAlertMsg(msg);
    setAlertType(type);
    setTimeout(() => setAlertMsg(null), 5000);
  };

  const loadData = useCallback(() => {
    setLoading(true);

    Promise.all([
      Repo.getStatoCodaAggiornamento(apiToken),
      Repo.getUtentiPerAnagrafica(apiToken, 1, 100),
      Repo.getUtentiPerIsee(apiToken, 1, 100),
      Repo.getChangelogAggiornamento(apiToken, null, 1, 50, false),
    ])
      .then(([stato, anagrafica, isee, cl]) => {
        setStatoCoda(stato.data);
        setUtentiAnagrafica(anagrafica.data || []);
        setUtentiIsee(isee.data || []);
        setChangelog(cl.data || []);
      })
      .catch((err) => {
        console.error(err);
        showAlert("Errore nel caricamento dei dati.", "danger");
      })
      .finally(() => setLoading(false));
  }, [apiToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAvviaAnpr = () => {
    setLoading(true);
    Repo.avviaCodaAnpr(apiToken, limitAnpr || null)
      .then(() => {
        showAlert("Coda ANPR creata con successo.");
        loadData();
      })
      .catch((err) => {
        console.error(err);
        showAlert("Errore durante la creazione della coda ANPR.", "danger");
        setLoading(false);
      });
  };

  const handleAvviaInps = () => {
    setLoading(true);
    Repo.avviaCodaInps(apiToken, limitInps || null)
      .then(() => {
        showAlert("Coda INPS creata con successo.");
        loadData();
      })
      .catch((err) => {
        console.error(err);
        showAlert("Errore durante la creazione della coda INPS.", "danger");
        setLoading(false);
      });
  };

  const loadChangelog = (tipo, erroriOnly) => {
    const tipoVal = tipo === "" ? null : Number(tipo);
    Repo.getChangelogAggiornamento(apiToken, tipoVal, 1, 50, erroriOnly)
      .then((res) => setChangelog(res.data || []))
      .catch(console.error);
  };

  const handleChangelogFilter = (e) => {
    const val = e.target.value;
    setChangelogFilter(val);
    loadChangelog(val, soloErrori);
  };

  const handleSoloErrori = (e) => {
    const val = e.target.checked;
    setSoloErrori(val);
    loadChangelog(changelogFilter, val);
  };

  if (loading) return <Loader />;

  return (
    <>
      <h1>AGGIORNAMENTI MASSIVI</h1>
      <div className="clearfix col-lg-10 py-lg-2">
        <p>
          Aggiorna in blocco i dati anagrafici (ANPR) e l&apos;ISEE (INPS) dei
          cittadini nella cartella sociale.
        </p>
      </div>

      {alertMsg && (
        <div className={`alert alert-${alertType}`} role="alert">
          {alertMsg}
        </div>
      )}

      {/* Stato coda */}
      {statoCoda && (
        <div className="row mb-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Stato coda aggiornamenti</h5>
              <div className="d-flex gap-4 flex-wrap">
                <span className="mr-3 text-warning">
                  <strong>In attesa:</strong> {statoCoda.inAttesa}
                </span>
                <span className="mr-3 text-success">
                  <strong>Completati:</strong> {statoCoda.completati}
                </span>
                <span className="mr-3 text-danger">
                  <strong>Errori:</strong> {statoCoda.errori}
                </span>
                <span className="text-primary">
                  <strong>Chiamate rimaste oggi:</strong>{" "}
                  {statoCoda.chiamateRimasteOggi} / {statoCoda.maxCallsPerDay}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row mb-4">
        {/* Sezione ANPR */}
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Aggiornamento ANPR (Anagrafica)</h5>
              <p className="text-muted small">
                Utenti ordinati per data aggiornamento anagrafica più vecchia.
              </p>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Quantità da aggiornare
                </label>
                <div className="d-flex gap-3 flex-wrap">
                  {LIMIT_OPTIONS.map((opt) => (
                    <div key={opt.value} className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="limitAnpr"
                        id={`anpr_${opt.value}`}
                        value={opt.value}
                        checked={limitAnpr === opt.value}
                        onChange={() => setLimitAnpr(opt.value)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`anpr_${opt.value}`}
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleAvviaAnpr}>
                Avvia aggiornamento ANPR
              </button>

              <div className="mt-4">
                <h6>Utenti (ordinati per anagrafica più vecchia)</h6>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Cognome e Nome</th>
                        <th>Ultimo aggiornamento</th>
                        <th>Deceduto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {utentiAnagrafica.map((u) => (
                        <tr key={u.id}>
                          <td>{u.nomeCompleto}</td>
                          <td>
                            {u.ultimoAggiornamentoAnagrafica
                              ? new Date(
                                  u.ultimoAggiornamentoAnagrafica,
                                ).toLocaleDateString("it-IT")
                              : "-"}
                          </td>
                          <td>
                            {u.deceduto ? (
                              <span className="badge bg-secondary">Sì</span>
                            ) : (
                              "No"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sezione ISEE */}
        <div className="col-lg-6 mt-4 mt-lg-0">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Aggiornamento INPS (ISEE)</h5>
              <p className="text-muted small">
                Utenti ordinati per anno ISEE più vecchio e data inserimento.
              </p>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Quantità da aggiornare
                </label>
                <div className="d-flex gap-3 flex-wrap">
                  {LIMIT_OPTIONS.map((opt) => (
                    <div key={opt.value} className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="limitInps"
                        id={`inps_${opt.value}`}
                        value={opt.value}
                        checked={limitInps === opt.value}
                        onChange={() => setLimitInps(opt.value)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`inps_${opt.value}`}
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <button className="btn btn-success" onClick={handleAvviaInps}>
                Avvia aggiornamento INPS
              </button>

              <div className="mt-4">
                <h6>Utenti (ordinati per ISEE più vecchio)</h6>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Cognome e Nome</th>
                        <th>Anno ISEE recente</th>
                        <th>Data inserimento ISEE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {utentiIsee.map((u) => (
                        <tr key={u.id}>
                          <td>{u.nomeCompleto}</td>
                          <td>{u.annoIseeRecente ?? "-"}</td>
                          <td>
                            {u.dataInsertIseeRecente
                              ? new Date(
                                  u.dataInsertIseeRecente,
                                ).toLocaleDateString("it-IT")
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Changelog */}
      <div className="row mt-2">
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="mb-3 flex-wrap">
              <div className="row">
                <div className="col-8">
                  <h5 className="card-title mb-0">Changelog aggiornamenti</h5>
                </div>
                <div className="col-1">
                  <div className="d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="soloErroriCheck"
                      checked={soloErrori}
                      onChange={handleSoloErrori}
                    />
                    <label
                      className="form-check-label text-danger fw-semibold mt-2 pr-3"
                      htmlFor="soloErroriCheck"
                    >
                      Solo errori
                    </label>
                  </div>
                </div>
                <div className="col-3">
                  <select
                    id="mese"
                    name="mese"
                    className="form-control custom-textarea"
                    value={changelogFilter}
                    onChange={handleChangelogFilter}
                    disabled={soloErrori}
                  >
                    <option value="">Tutti i tipi</option>
                    <option value="0">Solo ANPR</option>
                    <option value="1">Solo INPS</option>
                  </select>
                </div>
              </div>
            </div>
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <table className="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Cittadino</th>
                    <th>Tipo</th>
                    <th>Campo</th>
                    <th>Precedente</th>
                    <th>Nuovo</th>
                  </tr>
                </thead>
                <tbody>
                  {changelog.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        Nessun aggiornamento registrato
                      </td>
                    </tr>
                  ) : (
                    changelog.map((c) => {
                      const isErrore = c.campoModificato === "ERRORE";
                      return (
                        <tr
                          key={c.id}
                          className={isErrore ? "table-danger" : ""}
                        >
                          <td>
                            {new Date(c.dataInsert).toLocaleDateString("it-IT")}{" "}
                            {new Date(c.dataInsert).toLocaleTimeString(
                              "it-IT",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </td>
                          <td>{c.nomeCompleto}</td>
                          <td>
                            <span
                              className={`badge ${
                                c.tipo === 0
                                  ? "bg-primary text-white"
                                  : "bg-success text-white"
                              }`}
                            >
                              {TIPO_LABEL[c.tipo] ?? c.tipo}
                            </span>
                          </td>
                          <td>
                            {isErrore ? (
                              <span className="badge bg-danger">ERRORE</span>
                            ) : (
                              c.campoModificato
                            )}
                          </td>
                          <td>{c.valorePrecedente || "-"}</td>
                          <td
                            className={
                              isErrore
                                ? "text-danger fw-semibold"
                                : "text-success"
                            }
                          >
                            {c.nuovoValore || "-"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AggiornamentoMassivo;
